import { Timestamp } from 'firebase/firestore';

export type UserRole = 'customer' | 'admin' | 'platformOwner';

export type OrderStatus =
  | 'pending'
  | 'preparing'
  | 'ready'
  | 'served'
  | 'closed'
  | 'cancelled';

export type TableCallStatus = 'requested' | 'acknowledged' | 'resolved';

export interface AppSession {
  role: UserRole;
  restaurantId: string;
  tableId?: string;
  uid: string;
}

export interface SectionLabel {
  name: string;
  value: string;
}

export interface MenuItem {
  id: string;
  title: string;
  description: string;
  section: string;
  sections?: string[];
  price: number;
  img: string;
  availableQuantity: number | null;
  sortOrder?: number;
}

export interface MenuItemInput {
  title: string;
  description: string;
  section: string;
  sections: string[];
  price: number;
  img: string;
  availableQuantity: number | null;
  sortOrder: number;
}

export interface ManagedRestaurant {
  id: string;
  name: string;
  active: boolean;
  createdAt: number | null;
}

export interface ManagedTableToken {
  id: string;
  restaurantId: string;
  tableId: string;
  active: boolean;
  suspendedByRestaurant: boolean;
  createdAt: number | null;
}

export interface RestaurantMenu {
  name?: string;
  addToCartButtonLabel: string;
  buttonShowOrderLabel: string;
  continueOrderButtonLabel: string;
  clearOrderButtonLabel: string;
  addNoteButtonLabel: string;
  finishOrderButtonLabel: string;
  emptySearchText: string;
  modalHeaderTitle: string;
  totalSumLabel: string;
  noteTitle: string;
  noteButtonsLabels: {
    cancel: string;
    confirm: string;
  };
  sectionLabels: SectionLabel[];
  items: MenuItem[];
}

export interface CartItem extends MenuItem {
  quantity: number;
}

export interface CartOrder {
  cart: CartItem[];
  note: string;
}

export interface OrderItem {
  id: string;
  title: string;
  description: string;
  section: string;
  img: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  delivered: boolean;
}

export interface RestaurantOrder {
  id: string;
  tableId: string;
  status: OrderStatus;
  active: boolean;
  items: OrderItem[];
  note: string;
  total: number;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  closedAt?: Timestamp | null;
  adjustedAt?: Timestamp | null;
}

export interface TableCall {
  id: string;
  tableId: string;
  status: TableCallStatus;
  requestedAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Čeka potvrdu',
  preparing: 'U pripremi',
  ready: 'Pripremljeno',
  served: 'Isporučeno',
  closed: 'Zatvoreno',
  cancelled: 'Otkazano',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'medium',
  preparing: 'secondary',
  ready: 'success',
  served: 'primary',
  closed: 'dark',
  cancelled: 'danger',
};

export const EMPTY_MENU: RestaurantMenu = {
  addToCartButtonLabel: 'Dodaj u listu',
  buttonShowOrderLabel: 'Pogledaj porudžbinu',
  continueOrderButtonLabel: 'Nazad',
  clearOrderButtonLabel: 'Obriši sve',
  addNoteButtonLabel: 'Dodaj napomenu',
  finishOrderButtonLabel: 'Poruči',
  emptySearchText: 'Nema pronađenih artikala!',
  modalHeaderTitle: 'Porudžbina',
  totalSumLabel: 'Ukupno:',
  noteTitle: 'Napomena:',
  noteButtonsLabels: {
    cancel: 'Otkaži',
    confirm: 'Dodaj',
  },
  sectionLabels: [],
  items: [],
};
